import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useFlag } from "../../hooks/useFlag";
import { FlagKey } from "../../lib/featureFlags";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Squares2X2Icon,
  LinkIcon,
  DocumentTextIcon,
  DocumentChartBarIcon,
  ChartBarIcon,
  ArchiveBoxIcon,
  Cog6ToothIcon,
  ArrowRightStartOnRectangleIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ShieldCheckIcon,
  QuestionMarkCircleIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import Button from "../ui/Button";
import VeinWordmark from "../ui/VeinWordmark";

// Public documentation site. Also linked from the API Keys panel in Settings.
const DOCS_URL = "https://docs.vein.finance";

interface SidebarProps {
  mode: "startup" | "vc";
  /** Icon-only rail. Owned by App, which also shifts the content margin. */
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

interface NavItem {
  name: string;
  icon: React.ReactElement;
  page: string;
  badge?: number;
  flag?: FlagKey;
}

interface NavSection {
  type: "section" | "standalone";
  name: string;
  icon?: React.ReactElement;
  page?: string;
  items?: NavItem[];
  flag?: FlagKey;
}

const Sidebar: React.FC<SidebarProps> = ({ mode, collapsed = false, onToggleCollapse }) => {
  const { logout, user } = useAuth();
  const flagState: Record<FlagKey, boolean> = {
    portfolio_invites: useFlag("portfolio_invites"),
    public_api: useFlag("public_api"),
    ai_features: useFlag("ai_features"),
    live_connections: useFlag("live_connections"),
    portfolio_reports: useFlag("portfolio_reports"),
    founder_mcp: useFlag("founder_mcp"),
  };
  const visible = <T extends { flag?: FlagKey }>(items: T[]) =>
    items.filter((item) => !item.flag || flagState[item.flag]);
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize expanded sections from localStorage and auto-expand section with current page
  useEffect(() => {
    const saved = localStorage.getItem("vein-sidebar-sections");
    let initialState: Record<string, boolean> = {};

    if (saved) {
      try {
        initialState = JSON.parse(saved);
      } catch (e) {
        // Ignore parse errors
      }
    }

    // Auto-expand the section containing the current page
    if (mode === "startup") {
      for (const section of startupNavigation) {
        if (section.type === "section" && section.items) {
          const hasActivePage = section.items.some(
            (item) => location.pathname === item.page,
          );
          if (hasActivePage) {
            initialState[section.name] = true;
          }
        }
      }
    }

    setExpandedSections(initialState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, location.pathname]);

  // Save expanded sections to localStorage
  const toggleSection = (sectionName: string) => {
    setExpandedSections((prev) => {
      const newState = { ...prev, [sectionName]: !prev[sectionName] };
      localStorage.setItem("vein-sidebar-sections", JSON.stringify(newState));
      return newState;
    });
  };

  const handleLogout = () => {
    // Clear authentication data
    localStorage.removeItem("vein-auth-token");
    localStorage.removeItem("vein-user");
    localStorage.removeItem("vein-mode");
    localStorage.removeItem("vein-sidebar-sections");

    // Call logout from auth context (this will redirect to login automatically)
    logout();
  };

  const iconClass = (active: boolean) =>
    `w-5 h-5 ${active ? "text-vein-red-500" : "text-gray-400"}`;

  // Nested navigation structure for startup mode
  const startupNavigation: NavSection[] = [
    {
      type: "standalone",
      name: "Dashboard",
      icon: (
        <Squares2X2Icon
          className={iconClass(location.pathname === "/startup")}
        />
      ),
      page: "/startup",
    },
    {
      type: "standalone",
      name: "Investors",
      icon: (
        <UserGroupIcon
          className={iconClass(location.pathname === "/startup/investors")}
        />
      ),
      page: "/startup/investors",
    },
    {
      type: "standalone",
      name: "Connections",
      icon: (
        <LinkIcon
          className={iconClass(location.pathname === "/startup/connections")}
        />
      ),
      page: "/startup/connections",
    },
    {
      type: "standalone",
      name: "Reporting",
      icon: (
        <ChartBarIcon
          className={iconClass(location.pathname === "/startup/reporting")}
        />
      ),
      page: "/startup/reporting",
    },
    {
      type: "standalone",
      name: "Vault",
      icon: (
        <ArchiveBoxIcon
          className={iconClass(location.pathname === "/startup/vault")}
        />
      ),
      page: "/startup/vault",
    },
    {
      type: "standalone",
      name: "Data Access",
      icon: (
        <ShieldCheckIcon
          className={iconClass(location.pathname === "/startup/data-access")}
        />
      ),
      page: "/startup/data-access",
      flag: "public_api",
    },
  ];

  const settingsItem = {
    type: "standalone" as const,
    name: "Settings",
    icon: (
      <Cog6ToothIcon
        className={iconClass(location.pathname.endsWith("/settings"))}
      />
    ),
    page: `/${mode}/settings`,
  };

  // Flat navigation for VC mode
  const vcItems: NavItem[] = [
    {
      name: "Dashboard",
      icon: (
        <ChartBarIcon className={iconClass(location.pathname === "/vc")} />
      ),
      page: "/vc",
    },
    {
      name: "Portfolio",
      icon: (
        <Squares2X2Icon
          className={iconClass(
            location.pathname === "/vc/portfolio" ||
            location.pathname.startsWith("/vc/companies/")
          )}
        />
      ),
      page: "/vc/portfolio",
    },
    {
      name: "Requests",
      icon: (
        <DocumentTextIcon
          className={iconClass(location.pathname === "/vc/reporting")}
        />
      ),
      page: "/vc/reporting",
    },
    {
      name: "Portfolio Reports",
      icon: (
        <DocumentChartBarIcon
          className={iconClass(location.pathname === "/vc/portfolio-reports")}
        />
      ),
      page: "/vc/portfolio-reports",
      flag: "portfolio_reports",
    },
    {
      name: "Data Access",
      icon: (
        <ShieldCheckIcon
          className={iconClass(location.pathname === "/vc/data-access")}
        />
      ),
      page: "/vc/data-access",
      flag: "public_api",
    },
  ];

  // Row shell shared by every nav entry so the collapsed rail lines up whether a
  // row is a link, a button, or the collapse toggle.
  const rowClass = (extra: string) =>
    `group w-full flex items-center rounded-lg transition-colors duration-150 ${
      collapsed ? "justify-center px-0 py-2" : "gap-2.5 px-3 py-2"
    } ${extra}`;

  const IconTile: React.FC<{ className: string; badge?: number; children: React.ReactNode }> = ({
    className,
    badge,
    children,
  }) => (
    <div className="relative flex-shrink-0">
      <div className={`flex items-center justify-center ${className}`}>{children}</div>
      {/* Collapsed rail has no room for a count, so the badge degrades to a dot. */}
      {collapsed && badge != null && badge > 0 && (
        <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-vein-border" />
      )}
    </div>
  );

  const NavButton: React.FC<{ item: NavItem; isCurrent: boolean }> = ({
    item,
    isCurrent,
  }) => (
    <Button
      variant="bare"
      onClick={() => navigate(item.page)}
      title={collapsed ? item.name : undefined}
      aria-label={collapsed ? item.name : undefined}
      aria-current={isCurrent ? "page" : undefined}
      className={rowClass(
        isCurrent ? "text-vein-red-500" : "text-gray-700 hover:bg-gray-100",
      )}
    >
      <IconTile badge={item.badge} className="">
        {item.icon}
      </IconTile>
      {!collapsed && (
        <>
          <div className="flex-1 text-left">
            <div
              className={`text-sm ${isCurrent ? "font-semibold" : "font-medium"}`}
            >
              {item.name}
            </div>
          </div>
          {item.badge != null && item.badge > 0 && (
            <span className="w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center flex-shrink-0">
              {item.badge > 9 ? "9+" : item.badge}
            </span>
          )}
        </>
      )}
    </Button>
  );

  const SectionHeader: React.FC<{ section: NavSection }> = ({ section }) => {
    const isExpanded = expandedSections[section.name] || false;

    return (
      <Button
        variant="bare"
        onClick={() => {
          // A section's children have nowhere to render on the rail, so opening
          // one from collapsed state expands the sidebar with it.
          if (collapsed) onToggleCollapse?.();
          toggleSection(section.name);
        }}
        aria-expanded={isExpanded}
        title={collapsed ? section.name : undefined}
        aria-label={collapsed ? section.name : undefined}
        className={rowClass(
          "text-gray-700 hover:bg-gray-100",
        )}
      >
        <IconTile className="">
          <ChevronRightIcon
            className={`w-4 h-4 transform transition-transform duration-200 ${
              isExpanded && !collapsed ? "rotate-90" : ""
            }`}
          />
        </IconTile>
        {!collapsed && (
          <div className="flex-1 text-left">
            <div className="text-sm font-semibold text-vein-text-primary group-hover:text-vein-text-primary">
              {section.name}
            </div>
          </div>
        )}
      </Button>
    );
  };

  const SectionItems: React.FC<{ section: NavSection }> = ({ section }) => {
    const isExpanded = expandedSections[section.name] || false;

    if (!section.items || collapsed) return null;

    return (
      <div
        className={`grid transition-all duration-300 ease-in-out ${isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}
      >
        <div className="overflow-hidden">
          <div className="ml-4 space-y-1 pt-1">
            {section.items.map((item) => {
              const isCurrent = location.pathname === item.page;
              return (
                <NavButton key={item.page} item={item} isCurrent={isCurrent} />
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <aside
        className={`peer/sidebar absolute left-6 top-6 bottom-6 bg-white rounded-xl flex flex-col transition-all duration-300 border border-vein-border z-10 overflow-hidden ${
          collapsed ? "w-[76px]" : "w-[240px]"
        }`}
      >
        {/* Content wrapper */}
        <div className="relative z-10 flex flex-col h-full">
          {/* Logo section */}
          <div className={`pt-6 pb-4 ${collapsed ? "px-3" : "px-6"}`}>
            <div
              className={collapsed ? "flex justify-center" : ""}
              title={collapsed ? user?.company?.name || "Vein" : undefined}
            >
              {collapsed ? (
                <img
                  src="/images/vein_red_v_transparent.png"
                  alt="Vein"
                  className="w-9 h-9 object-contain flex-shrink-0"
                />
              ) : (
                <VeinWordmark className="h-7 w-auto" />
              )}
              <h1 className="sr-only">Vein</h1>
              {user && !collapsed && (
                <div className="min-w-0 mt-4">
                  <p className="text-sm font-semibold text-vein-text-primary truncate leading-tight">
                    {user.name || user.email}
                  </p>
                  {/* Company name doubles as the persona cue: the page ground no
                      longer encodes investor vs company. */}
                  <p className="text-xs text-vein-text-tertiary truncate leading-tight mt-0.5">
                    {user.company?.name ?? (mode === "vc" ? "Investor" : "Company")}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Navigation */}
          <div className={`flex-1 py-2 overflow-y-auto overflow-x-hidden ${collapsed ? "px-3" : "px-6"}`}>
            <nav className="space-y-1">
              {mode === "startup"
                ? // Startup mode with collapsible sections
                  visible(startupNavigation).map((section) => {
                    if (section.type === "standalone" && section.page) {
                      const isCurrent = location.pathname === section.page;
                      return (
                        <NavButton
                          key={section.page}
                          item={{
                            name: section.name,
                            icon: section.icon!,
                            page: section.page,
                          }}
                          isCurrent={isCurrent}
                        />
                      );
                    } else if (section.type === "section") {
                      return (
                        <div key={section.name} className="space-y-1">
                          <SectionHeader section={section} />
                          <SectionItems section={section} />
                        </div>
                      );
                    }
                    return null;
                  })
                : // VC mode with flat navigation (unchanged)
                  visible(vcItems).map((item) => {
                    const isCurrent =
                      location.pathname === item.page ||
                      (location.pathname.startsWith("/vc/companies/") &&
                        item.page === "/vc/portfolio");
                    return (
                      <NavButton
                        key={item.page}
                        item={item}
                        isCurrent={isCurrent}
                      />
                    );
                  })}

            </nav>
          </div>

          {/* Bottom Actions */}
          <div className={`space-y-3 py-6 ${collapsed ? "px-3" : "px-6"}`}>
            {/* Help — external, so this is an anchor rather than a NavButton,
                which navigates within the app. */}
            <a
              href={DOCS_URL}
              target="_blank"
              rel="noopener noreferrer"
              title={collapsed ? "Help" : undefined}
              aria-label={collapsed ? "Help" : undefined}
              className={rowClass("text-gray-700 hover:bg-gray-100")}
            >
              <IconTile className="">
                <QuestionMarkCircleIcon
                  className="w-5 h-5 text-gray-400"
                  strokeWidth={2}
                  aria-hidden="true"
                />
              </IconTile>
              {!collapsed && (
                <div className="flex-1 text-left">
                  <div className="text-sm font-medium text-vein-text-secondary group-hover:text-vein-text-primary">
                    Help
                  </div>
                </div>
              )}
            </a>

            {/* Settings Button */}
            <NavButton
              item={{
                name: settingsItem.name,
                icon: settingsItem.icon,
                page: settingsItem.page,
              }}
              isCurrent={location.pathname === settingsItem.page}
            />

            {/* Logout Button */}
            <Button
              variant="bare"
              onClick={handleLogout}
              title={collapsed ? "Logout" : undefined}
              aria-label={collapsed ? "Logout" : undefined}
              className={rowClass(
                "text-gray-700 hover:bg-gray-100 hover:text-vein-red-500",
              )}
            >
              <IconTile className="">
                <ArrowRightStartOnRectangleIcon className="w-5 h-5 text-gray-400 group-hover:text-red-500" />
              </IconTile>
              {!collapsed && (
                <div className="flex-1 text-left">
                  <div className="text-sm font-medium text-gray-700 group-hover:text-red-600">
                    Logout
                  </div>
                </div>
              )}
            </Button>

          </div>
        </div>
      </aside>

      {/* Collapse handle — rides the sidebar's outer edge and stays out of the
          way until pointed at, so collapsing costs the nav no permanent row.
          Held at low opacity while collapsed, the one state where a reader who
          forgot about it would otherwise have nothing to aim for. */}
      {onToggleCollapse && (
        <Button
          variant="bare"
          onClick={onToggleCollapse}
          title={collapsed ? "Expand sidebar (])" : "Collapse sidebar ([)"}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-expanded={!collapsed}
          className={`absolute top-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-md flex items-center justify-center
            bg-white border border-vein-border
            hover:bg-gray-50 hover:scale-105
            focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-vein-red-500 focus-visible:ring-offset-1 
            peer-hover/sidebar:opacity-100 hover:opacity-100
            ${collapsed ? "left-[86px] opacity-60" : "left-[250px] opacity-0"} duration-300`}
        >
          <ChevronDoubleLeftIcon
            className={`w-3.5 h-3.5 text-gray-500 transform transition-transform duration-300 ${
              collapsed ? "rotate-180" : ""
            }`}
            aria-hidden="true"
          />
        </Button>
      )}
    </>
  );
};

export default Sidebar;
